import React, { useEffect } from "react";
import "./App.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { useConfig } from "./config";
import Navbar from "./components/Navbar";
import BotFrame from "./components/BotFrame";
import Admin from "./Admin";
import Login from "./Login";
import LoginVerify from "./LoginVerify";
import ApproveAccount from "./ApproveAccount";
import RequestAccount from "./RequestAccount";
import ProtectedRoute from "./components/ProtectedRoute";  // Import the ProtectedRoute
import { AuthProvider } from "./contexts/AuthContext";

// Create a client for React Query
const queryClient = new QueryClient();

function AppContent() {
  const { config, loading, error } = useConfig();

  if (loading) {
    
    return <div>Loading configuration...</div>;
        
  }

  if (error) {
    return <div>Error loading configuration: {error.message}</div>;
  }

  return (
    <AuthProvider>
    {/* <Router> */}
      <div className="app">
        <Navbar />
        <main className="main-content">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/login/verify" element={<LoginVerify />} />
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/admin" element={<ProtectedRoute element={<Admin />} />} />
            <Route path="/:slug" element={<ProtectedRoute element={<BotFrame />} />} />
            <Route path="/approve-account" element={<ApproveAccount />} />
            <Route path="/request-account" element={<RequestAccount />} />
          </Routes>
        </main>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 5000,
          }}
        />
      </div>
    {/* </Router> */}
    </AuthProvider>
  );
}

function App() {
  const { config, loading, error } = useConfig();
  const navigate = useNavigate();
  const location = useLocation();
  function setCookie(cname,cvalue,exdays) {
    const d = new Date();
    d.setTime(d.getTime() + (exdays*24*60*60*1000));
    let expires = "expires=" + d.toUTCString();
    document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
  }
  
  
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const user_id = params.get("user_id");
    const email = params.get("email");
    const magic_link_timestamp = params.get("timestamp"); // Add timestamp from URL
    const user_type = params.get("user_type"); // Add timestamp from URL

    if (magic_link_timestamp) {
      const currentTime = Date.now();
      const linkTime = parseInt(magic_link_timestamp);
      const fiveMinutesInMs = 5 * 60 * 1000; // 5 minutes in milliseconds

      if (currentTime - linkTime > fiveMinutesInMs) {
        // Magic link expired, redirect to login
        //console.log("Magic link expired");
        localStorage.removeItem("user_id");
        localStorage.removeItem("email");
        localStorage.removeItem("timestamp");
        navigate("/login");
        return;
      }
    }

    if (email) {
      fetch(`${config.API_BASE_URL}/api/auth/check-session?email=${encodeURIComponent(email)}`)
        .then(res => res.json())
        .then(data => {
          if (data.is_active === false) {
            
            // Remove user info and redirect to login
            localStorage.removeItem("user_id");
            localStorage.removeItem("email");
            navigate("/login");
          }
        })
        .catch(err => {
          // Optionally handle error (e.g., network error)
          //console.error("Failed to check user status:", err);
        });
    }

    let shouldUpdate = false;

    if (user_id && !localStorage.getItem("user_id")) {
      localStorage.setItem("user_id", user_id);
      shouldUpdate = true;
    }
    if (email && !localStorage.getItem("email")) {
      localStorage.setItem("email", email);
      shouldUpdate = true;
    }
    if(user_type && !localStorage.getItem("user_type")) {
      localStorage.setItem("user_type", user_type);
      shouldUpdate = true;
    }

    // Remove user_id and email from the URL if they were present
    if (shouldUpdate) {
      setCookie("user_id", user_id, 7);
      setCookie("email", email, 7);
      setCookie("user_type", user_type, 7);
      params.delete("user_id");
      params.delete("email");
      params.delete("timestamp");
      params.delete("user_type");
      const newSearch = params.toString();
      const newPath = location.pathname + (newSearch ? `?${newSearch}` : "");
      window.history.replaceState({}, "", newPath);
    }
  }, [location]);


  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "logout") {
        // Redirect to login page if logout is detected
        navigate("/login");
      }
    };

    // Add event listener for storage changes
    window.addEventListener("storage", handleStorageChange);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [navigate]);

  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  );
}

export default App;
